import {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import omit from 'lodash/omit';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {updateFewerComparablesReason} from 'ducks/fewerComparablesReason';
import {MIN_SOI_COMPARABLES, SOI_EXPLANATION_MAX_LENGTH} from 'constants/soi';
import './FewerComparablesReason.scss';

export const EXPLANATION_MAX_LENGTH = SOI_EXPLANATION_MAX_LENGTH;

export const DECLARATION_TEXT = 'I, the agent or agent\u2019s representative for the property offered for sale, ' +
  'did not take the sale price of three comparable properties into account in determining the estimated selling ' +
  'price for the property because I believe that fewer than three comparable properties were sold within the ' +
  'relevant period.';

export const EXPLANATION_LABEL = 'Explain the reason the agent or agent\u2019s representative believes that fewer ' +
  'than three comparable properties:';

const NO_ERRORS = {};

export default function FewerComparablesReason() {
  const dispatch = useDispatch();
  const showSection = useSelector((state) => !!state.featureFlags.propertyPriceStatement);
  const selectedCount = useSelector((state) => state.comparableSales.selected.length);
  const declared = useSelector((state) => !!state.fewerComparablesReason.declared);
  const explanation = useSelector((state) => state.fewerComparablesReason.explanation || '');
  const errors = useSelector((state) => state.fewerComparablesReason.errors || NO_ERRORS);

  const [draftExplanation, setDraftExplanation] = useState(explanation);
  const persistedExplanationRef = useRef(explanation);
  const draftExplanationRef = useRef(explanation);

  useEffect(() => {
    // Keep the input in sync when the stored preference is loaded or reset elsewhere.
    if (explanation !== draftExplanationRef.current) {
      setDraftExplanation(explanation);
      draftExplanationRef.current = explanation;
      persistedExplanationRef.current = explanation;
    }
  }, [explanation]);

  const persistExplanation = useCallback(() => {
    const value = draftExplanationRef.current;
    if (value !== persistedExplanationRef.current) {
      persistedExplanationRef.current = value;
      dispatch(updateFewerComparablesReason({explanation: value}, true));
    }
  }, [dispatch]);

  useEffect(() => persistExplanation, [persistExplanation]);

  const onDeclarationChange = useCallback((event) => {
    const {checked} = event.target;
    const payload = {declared: checked};
    if (checked && errors.declared) {
      payload.errors = omit(errors, 'declared');
    }
    dispatch(updateFewerComparablesReason(payload, true));
  }, [dispatch, errors]);

  const onExplanationChange = useCallback((event) => {
    const {value} = event.target;
    draftExplanationRef.current = value;
    setDraftExplanation(value);
    const payload = {explanation: value};
    if (value.trim() && errors.explanation) {
      payload.errors = omit(errors, 'explanation');
    }
    dispatch(updateFewerComparablesReason(payload));
  }, [dispatch, errors]);

  if (!showSection || selectedCount >= MIN_SOI_COMPARABLES) {
    return null;
  }

  return (
    <div className="fewer-comparables-reason" data-testid="fewer-comparables-reason">
      <Typography variant="fontWeight600" className="fewer-comparables-reason__heading">
        Reason there are fewer than three comparable properties
      </Typography>
      <FormControl
        error={!!errors.declared}
        className={
          'fewer-comparables-reason__declaration-control' +
          (errors.declared ? ' fewer-comparables-reason__declaration-control--error' : '')
        }
      >
        <FormControlLabel
          className="fewer-comparables-reason__declaration"
          control={
            <Checkbox
              checked={declared}
              onChange={onDeclarationChange}
              disableRipple
              sx={{
                '& .MuiSvgIcon-root': {fontSize: 20},
                '&:hover': {backgroundColor: 'transparent'},
              }}
              inputProps={{'aria-label': DECLARATION_TEXT, 'data-testid': 'fewer-comparables-reason-declaration'}}
            />
          }
          label={DECLARATION_TEXT}
        />
      </FormControl>
      <Typography
        component="label"
        htmlFor="fewer-comparables-reason-explanation"
        className="fewer-comparables-reason__explanation-label"
      >
        {EXPLANATION_LABEL}
      </Typography>
      <TextField
        id="fewer-comparables-reason-explanation"
        className="fewer-comparables-reason__explanation"
        fullWidth
        size="small"
        error={!!errors.explanation}
        helperText={errors.explanation || ''}
        FormHelperTextProps={{'data-testid': 'fewer-comparables-reason-explanation-error'}}
        value={draftExplanation}
        onChange={onExplanationChange}
        onBlur={persistExplanation}
        inputProps={{
          maxLength: EXPLANATION_MAX_LENGTH,
          'data-testid': 'fewer-comparables-reason-explanation',
        }}
      />
    </div>
  );
}
